import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  AppBar,
  Toolbar,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ArrowBack, Print, Block, CheckCircle, AttachFile, DriveFileRenameOutline, Undo } from '@mui/icons-material';
import { EasyCheckButton } from '../../common/Controls';
import { mobileChequeService } from '../../../services/mobile/mobileChequeService';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';
import MobileChequeAttachments from './MobileChequeAttachments';

const statusColors = {
  Pending: 'warning',
  Printed: 'info',
  Signed: 'secondary',
  Collected: 'success',
  Cleared: 'success',
  Cancelled: 'error'
};

const MobileChequeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cheque, setCheque] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [printPreferences, setPrintPreferences] = useState(null);

  // Void dialog
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  // Clear dialog
  const [clearOpen, setClearOpen] = useState(false);
  const [clearedDate, setClearedDate] = useState(new Date());

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    let cancelled = false;

    const fetchCheque = async () => {
      setCheque(null);
      setLoading(true);
      try {
        const result = await mobileChequeService.getChequeById(id);
        if (cancelled) return;
        if (result.success && result.data) {
          setCheque(result.data);
        }
      } catch {
        // handled by service layer
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) fetchCheque();

    return () => { cancelled = true; };
  }, [id]);

  // Fetch company print preferences on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      const prefs = await mobileChequeService.getCompanyPreferences();
      if (prefs) setPrintPreferences(prefs);
    };
    fetchPreferences();
  }, []);

  const handlePrint = async () => {
    setActionLoading(true);
    const result = await mobileChequeService.printChequeLetterSize(id, printPreferences || {});
    if (!result.success) {
      setSnackbar({ open: true, message: result.message || 'Failed to print cheque.', severity: 'error' });
    }
    setActionLoading(false);
  };

  const handleVoid = async () => {
    setActionLoading(true);
    const result = await mobileChequeService.voidCheque(id, voidReason);
    if (result.success) {
      setCheque((prev) => ({ ...prev, Status: 'Cancelled', status: 'Cancelled' }));
      setVoidOpen(false);
      setVoidReason('');
    }
    setActionLoading(false);
  };

  const handleMarkCleared = async () => {
    setActionLoading(true);
    const dateStr = clearedDate instanceof Date
      ? clearedDate.toISOString().split('T')[0]
      : clearedDate;
    const result = await mobileChequeService.markCleared([parseInt(id)], dateStr);
    if (result.success) {
      setCheque((prev) => ({ ...prev, Status: 'Cleared', status: 'Cleared' }));
      setClearOpen(false);
    }
    setActionLoading(false);
  };

  const handleMarkSigned = async () => {
    setActionLoading(true);
    const result = await mobileChequeService.markSigned([parseInt(id)]);
    if (result.success) {
      setCheque((prev) => ({ ...prev, Status: 'Signed', status: 'Signed' }));
    } else {
      setSnackbar({ open: true, message: result.message || 'Failed to mark as signed.', severity: 'error' });
    }
    setActionLoading(false);
  };

  const handleMarkUnsigned = async () => {
    setActionLoading(true);
    const result = await mobileChequeService.markUnsigned([parseInt(id)]);
    if (result.success) {
      setCheque((prev) => ({ ...prev, Status: 'Printed', status: 'Printed' }));
    } else {
      setSnackbar({ open: true, message: result.message || 'Failed to revert to printed.', severity: 'error' });
    }
    setActionLoading(false);
  };

  const formatAmount = (amount, currency = 'AED') =>
    formatCurrencyAmount(amount, currency, { withSymbol: false });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#2563eb' }} />
      </Box>
    );
  }

  if (!cheque) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="body1" color="text.secondary">Cheque not found.</Typography>
        <EasyCheckButton onClick={() => navigate('/mobile/cheques')} sx={{ mt: 2, color: '#2563eb' }}>
          Back to Cheques
        </EasyCheckButton>
      </Box>
    );
  }

  const status = cheque.Status || cheque.status || 'Pending';
  const isPending = status === 'Pending';
  const isPrinted = status === 'Printed';
  const isSigned = status === 'Signed';
  const signedStatusEnabled = Boolean(printPreferences?.enableSignedStatus);
  const canPrint = isPending || isPrinted || isSigned;
  const isCollected = status === 'Collected';
  const canVoid = isPending || isPrinted || isSigned || isCollected;
  const canClear = isPrinted || isSigned || isCollected;
  const canSign = signedStatusEnabled && isPrinted;
  const canUnsign = signedStatusEnabled && isSigned;

  const details = [
    { label: 'Beneficiary', value: cheque.BeneficiaryName || cheque.beneficiaryName },
    { label: 'Amount', value: formatAmount(cheque.ChqAmount || cheque.chqAmount, cheque.Currency || cheque.currency), bold: true },
    { label: 'Cheque Number', value: cheque.ChequeNumber || cheque.chequeNumber },
    { label: 'Bank', value: cheque.BankName || cheque.bankName },
    { label: 'Date', value: formatDate(cheque.ChqDate || cheque.chqDate) },
    { label: 'Remarks', value: cheque.Remarks || cheque.remarks || '-' }
  ];

  return (
    <Box>
      {/* Back Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/mobile/cheques')} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', flex: 1 }}>
          Cheque Details
        </Typography>
        <Chip
          label={status}
          color={statusColors[status] || 'default'}
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Details Card */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          {details.map((item, index) => (
            <Box key={item.label}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: item.bold ? 700 : 500,
                    color: item.bold ? '#2563eb' : '#0f172a',
                    textAlign: 'right',
                    maxWidth: '60%',
                    wordBreak: 'break-word'
                  }}
                >
                  {item.value || '-'}
                </Typography>
              </Box>
              {index < details.length - 1 && <Divider />}
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Attachments Section */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AttachFile sx={{ fontSize: 20, color: '#2563eb' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Attachments
            </Typography>
          </Box>
          <MobileChequeAttachments
            chequeId={parseInt(id)}
            readOnly={status === 'Cancelled'}
            onError={(msg) => setSnackbar({ open: true, message: msg, severity: 'error' })}
            onSuccess={(msg) => setSnackbar({ open: true, message: msg, severity: 'success' })}
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {canPrint && (
          <EasyCheckButton
            fullWidth
            onClick={handlePrint}
            disabled={actionLoading}
            startIcon={<Print />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' },
              fontWeight: 600
            }}
          >
            Print Cheque
          </EasyCheckButton>
        )}

        {canClear && (
          <EasyCheckButton
            fullWidth
            onClick={() => setClearOpen(true)}
            disabled={actionLoading}
            startIcon={<CheckCircle />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              backgroundColor: '#10b981',
              '&:hover': { backgroundColor: '#059669' },
              fontWeight: 600
            }}
          >
            Mark Cleared
          </EasyCheckButton>
        )}

        {canSign && (
          <EasyCheckButton
            fullWidth
            onClick={handleMarkSigned}
            disabled={actionLoading}
            startIcon={<DriveFileRenameOutline />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              backgroundColor: '#7e57c2',
              '&:hover': { backgroundColor: '#5e35b1' },
              fontWeight: 600
            }}
          >
            Mark as Signed
          </EasyCheckButton>
        )}

        {canUnsign && (
          <EasyCheckButton
            fullWidth
            onClick={handleMarkUnsigned}
            disabled={actionLoading}
            startIcon={<Undo />}
            variant="outlined"
            sx={{
              py: 1.5,
              borderRadius: 2,
              color: '#5e35b1',
              borderColor: '#5e35b1',
              fontWeight: 600
            }}
          >
            Revert to Printed
          </EasyCheckButton>
        )}

        {canVoid && (
          <EasyCheckButton
            fullWidth
            onClick={() => setVoidOpen(true)}
            disabled={actionLoading}
            startIcon={<Block />}
            variant="outlined"
            sx={{
              py: 1.5,
              borderRadius: 2,
              color: '#ef4444',
              borderColor: '#ef4444',
              '&:hover': { borderColor: '#dc2626', backgroundColor: '#fef2f2' },
              fontWeight: 600
            }}
          >
            Void Cheque
          </EasyCheckButton>
        )}
      </Box>

      {/* Void Dialog */}
      <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Void Cheque</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
            Are you sure you want to void this cheque? This action cannot be undone.
          </Typography>
          <TextField
            fullWidth
            label="Reason for voiding"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            multiline
            rows={2}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <EasyCheckButton onClick={() => setVoidOpen(false)} sx={{ color: '#64748b' }}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            onClick={handleVoid}
            disabled={actionLoading}
            sx={{ color: '#ef4444', fontWeight: 600 }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Void'}
          </EasyCheckButton>
        </DialogActions>
      </Dialog>

      {/* Clear Dialog */}
      <Dialog open={clearOpen} onClose={() => setClearOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Mark as Cleared</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
            Select the date this cheque was cleared.
          </Typography>
          <DatePicker
            value={clearedDate}
            onChange={(date) => setClearedDate(date)}
            label="Cleared Date"
            slotProps={{
              textField: {
                fullWidth: true,
                InputProps: { sx: { borderRadius: 2 } }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <EasyCheckButton onClick={() => setClearOpen(false)} sx={{ color: '#64748b' }}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            onClick={handleMarkCleared}
            disabled={actionLoading}
            sx={{ color: '#10b981', fontWeight: 600 }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Confirm'}
          </EasyCheckButton>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MobileChequeDetail;
